import { Component, Input, HostListener, HostBinding, ChangeDetectionStrategy, forwardRef, ChangeDetectorRef, Optional, Inject } from '@angular/core'
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms'
import { IdentityService } from 'main/services/identity.service'
import { noop } from 'rxjs'

@Component({
  selector: 'checkbox',
  templateUrl: './checkbox.component.pug',
  styleUrls: ['./checkbox.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CheckboxComponent),
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CheckboxComponent implements ControlValueAccessor {
  @Input() text?: string
  @Input() invert?: boolean
  @Input() disabled?: boolean
  @HostBinding('attr.aria-role') accessibilityRole = 'checkbox'
  @HostBinding('attr.disabled') get disabledAttribute () {
    return this.disabled || this.controlDisabled ? '' : null
  }

  private _value = false
  controlDisabled = false

  constructor (
    private changeDetector: ChangeDetectorRef,
    @Optional() @Inject(IdentityService) private identity: IdentityService | null,
  ) { }

  @Input('checked') get value () { return this._value }

  set value (_value: boolean) {
    this._value = _value
    this.onChangeHandler(_value)
    this.onTouchHandler(_value)
  }

  @HostListener('keydown', ['$event'])
  onKeyDown (event: KeyboardEvent) {
    if (event.key === 'Enter' || event.key === 'Space') {
      this.toggleValue()
      event.preventDefault()
      event.stopPropagation()
    }
  }

  @HostListener('click')
  toggleValue () {
    if (this.disabled || this.controlDisabled || !this.ngModelMode) {
      return
    }
    this.writeValue(!this.value)
  }

  @HostBinding('attr.checked') get displayValue () {
    return !this.invert !== !this.value
  }

  // ControlValueAccessor
  private ngModelMode = false
  private onChangeHandler: any = noop
  private onTouchHandler: any = noop

  writeValue (value: boolean) {
    if (this.value !== value) {
      this.changeDetector.markForCheck()
    }
    this.value = !!value
  }

  registerOnChange (fn) {
    this.ngModelMode = true
    this.onChangeHandler = fn
  }

  registerOnTouched (fn) {
    this.onTouchHandler = fn
  }

  setDisabledState (isDisabled) {
    this.controlDisabled = isDisabled
    this.changeDetector.markForCheck()
  }

  // HACK: temporary implementation of permissionEnable directive for ngUpgrade templates
  @Input('permissionenable') requirePermission?: string

  ngAfterViewInit () {
    if (this.requirePermission) {
      if (this.identity && !this.identity.hasPermission(this.requirePermission)) {
        this.setDisabledState(true)
      }
    }
  }
}
